import { CommonModule, DOCUMENT } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostBinding,
	inject,
	OnInit,
	Output,
	Renderer2,
	RendererStyleFlags2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import {
	CapacitorSecurityProvider,
	SecurityProviderStatus,
} from '@capacitor-community/security-provider';
import FontFaceObserver from 'fontfaceobserver';
import { BehaviorSubject, combineLatest, Observable, timer } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { Theme } from '@reach/interfaces/settings/theme.enum';

import { ClientService, SessionService, SettingsService } from '~app-client/core/services';
import { COMMON_CLIENT_CONFIG } from '~ng-common-lib/core/tokens/client-config';
import { LottieAnimationComponent } from '~ng-common-lib/ui/animations/lottie-animation.component';

export enum ItemsToLoad {
	FONT = 'FONT',
	ICONS = 'ICONS',
	SESSION = 'SESSION',
	SETTINGS = 'SETTINGS',
	ANDROID_SECURITY = 'ANDROID_SECURITY',
}

enum ItemsToLoadState {
	LOADING = 'loading',
	FAILED = 'failed',
	FINISHED = 'finished',
}

@Component({
	selector: 'app-initialization',
	templateUrl: './app-initialization.component.html',
	styleUrls: ['./app-initialization.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, LottieAnimationComponent],
})
export class AppInitializationComponent implements OnInit {
	private readonly stateMap: Record<ItemsToLoad, BehaviorSubject<ItemsToLoadState>> = {
		[ItemsToLoad.FONT]: new BehaviorSubject(ItemsToLoadState.LOADING),
		[ItemsToLoad.ICONS]: new BehaviorSubject(ItemsToLoadState.LOADING),
		[ItemsToLoad.SESSION]: new BehaviorSubject(ItemsToLoadState.LOADING),
		[ItemsToLoad.SETTINGS]: new BehaviorSubject(ItemsToLoadState.LOADING),
		[ItemsToLoad.ANDROID_SECURITY]: new BehaviorSubject(ItemsToLoadState.LOADING),
	};

	private getState$(key: ItemsToLoad): Observable<ItemsToLoadState> {
		return this.stateMap[key].asObservable();
	}

	private readonly clientConfig = inject(COMMON_CLIENT_CONFIG);

	private readonly logoLoaded$$ = new BehaviorSubject<boolean>(false);
	public readonly logoLoaded$ = this.logoLoaded$$.asObservable();

	public readonly useLottieInitAnimation =
		this.clientConfig.branding.loader.initVariant === 'lottie';
	public readonly initLottiePath =
		this.clientConfig.branding.loader.initLottiePath ??
		'/assets/animations/Olympic_Rings-to-AI.json';

	@HostBinding('class.init-theme-olympics')
	public readonly isOlympicsInitTheme =
		this.clientConfig.branding.loader.initTheme === 'olympics';

	public readonly loaderVisible$ = this.logoLoaded$.pipe(
		filter((logoLoaded) => logoLoaded),
		debounceTime(1000)
	);

	public readonly itemsFinished$ = combineLatest(
		Object.values(ItemsToLoad).map((key) => {
			return this.getState$(key).pipe(map((state) => ({ key, state })));
		})
	).pipe(
		map((states) => {
			return states.filter((item) => item.state !== ItemsToLoadState.LOADING);
		}),
		tap((finishedStates) => {
			console.debug(
				'finished items:',
				finishedStates
					.filter((item) => item.state === ItemsToLoadState.FINISHED)
					.map((item) => item.key)
					.join(', ')
			);
		}),
		filter((finishedStates) => {
			return finishedStates.length === Object.values(ItemsToLoad).length;
		}),
		map((finishedStates) => {
			return finishedStates
				.filter((item) => item.state === ItemsToLoadState.FAILED)
				.map((items) => items.key);
		})
	);

	public readonly animationFinished$ = this.loaderVisible$.pipe(
		filter((visible) => visible),
		switchMap(() => {
			return timer(1000);
		}),
		switchMap(() => this.itemsFinished$)
	);

	private readonly destroyRef = inject(DestroyRef);
	private readonly renderer = inject(Renderer2);
	private readonly client = inject(ClientService);
	private readonly sessionService = inject(SessionService);
	private readonly settingsService = inject(SettingsService);
	private readonly document = inject(DOCUMENT);
	private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	@Output('close')
	protected readonly close$$ = new EventEmitter<ItemsToLoad[]>();

	public async ngOnInit(): Promise<void> {
		void this.handleFontsCheck();
		void this.checkSession();

		if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
			const result = await CapacitorSecurityProvider.installIfNeeded();
			if (
				result.status !== SecurityProviderStatus.Success &&
				result.status != SecurityProviderStatus.NotImplemented
			) {
				this.stateMap[ItemsToLoad.ANDROID_SECURITY].next(ItemsToLoadState.FAILED);
			} else {
				this.stateMap[ItemsToLoad.ANDROID_SECURITY].next(ItemsToLoadState.FINISHED);
			}
		} else {
			this.stateMap[ItemsToLoad.ANDROID_SECURITY].next(ItemsToLoadState.FINISHED);
		}

		this.animationFinished$
			.pipe(
				switchMap((failedKeys) => {
					return timer(1000).pipe(map(() => failedKeys));
				}),
				take(1),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.finish();
			});
	}

	public async logoLoaded(): Promise<void> {
		try {
			await SplashScreen.hide({ fadeOutDuration: 100 });
		} catch {
			return;
		}
		this.logoLoaded$$.next(true);

		const loaderWrapper = this.document.querySelector('#indexLoaderWrapper');
		loaderWrapper?.remove();
	}

	private async checkSession(): Promise<void> {
		try {
			if (this.sessionService.shouldSkipSessionPing()) {
				this.stateMap.SESSION.next(ItemsToLoadState.FINISHED);
				this.stateMap.SETTINGS.next(ItemsToLoadState.FAILED);
				return;
			}

			await this.sessionService.ping();

			this.stateMap.SESSION.next(ItemsToLoadState.FINISHED);
			void this.checkSettings();
		} catch {
			this.stateMap.SESSION.next(ItemsToLoadState.FAILED);
			this.stateMap.SETTINGS.next(ItemsToLoadState.FAILED);
		}
	}

	private async checkSettings(): Promise<void> {
		try {
			await this.settingsService.load();
			this.stateMap.SETTINGS.next(ItemsToLoadState.FINISHED);

			const theme = this.settingsService.settings.general.theme;
			const browserIsDark = this.client.browserTheme === Theme.DARK;

			const useDark = theme === Theme.DARK || (theme === Theme.AUTO && browserIsDark);
			const themeName = useDark ? 'dark' : 'light';

			this.renderer.setStyle(
				this.document.documentElement,
				'--color-bg-current',
				`rgb(var(--${themeName}-color-bg-180-rgb))`,
				RendererStyleFlags2.DashCase
			);
		} catch {
			this.stateMap.SETTINGS.next(ItemsToLoadState.FAILED);
		}
	}

	private async handleFontsCheck(): Promise<void> {
		const icomoonFont = new FontFaceObserver('icomoon');

		this.testFont(ItemsToLoad.ICONS, icomoonFont);

		try {
			// https://developer.mozilla.org/en-US/docs/Web/API/Document/fonts
			await this.document.fonts.ready;
			this.stateMap[ItemsToLoad.FONT].next(ItemsToLoadState.FINISHED);
		} catch {
			// Fallback
			const helveticaFont = new FontFaceObserver('Helvetica Neue');
			const interFont = new FontFaceObserver('Inter');
			this.testFont(ItemsToLoad.FONT, helveticaFont);
			this.testFont(ItemsToLoad.FONT, interFont);
		}
	}

	private testFont(key: ItemsToLoad, font: FontFaceObserver, count = 0): void {
		const MAX_TIME_MS = 30000; // 30s
		const TIMEOUT_MS = 300;
		const MAX_COUNT = Math.ceil(MAX_TIME_MS / TIMEOUT_MS);
		font.load('test', TIMEOUT_MS)
			.then(() => {
				this.stateMap[key].next(ItemsToLoadState.FINISHED);
			})
			.catch(() => {
				if (this.stateMap[key].value === ItemsToLoadState.LOADING) {
					if (count < MAX_COUNT) {
						this.testFont(key, font, count + 1);
					} else {
						this.stateMap[key].next(ItemsToLoadState.FAILED);
					}
				}
			})
			.finally(() => {
				return;
			});
	}

	private finish(): void {
		const failingItems = Object.values(ItemsToLoad).filter((key) => {
			return this.stateMap[key]?.value === ItemsToLoadState.FAILED;
		});
		this.close$$.emit(failingItems);
		this.elementRef.nativeElement.remove();
	}
}
